import React, { useState, useEffect, useMemo } from 'react';
import { matchesSearch } from '../utils/search';
import { useIsMobile } from '../hooks/useIsMobile';
import { usePageTitle } from '../hooks/usePageTitle';
import { usersAPI } from '../services/api';
import { useBatchedList } from '../hooks/useBatchedList';
import UserForm from '../components/users/UserForm';
import InvitationsPanel from '../components/users/InvitationsPanel';
import DeleteConfirmModal from '../components/ui/DeleteConfirmModal';
import LazyLoadTrigger from '../components/ui/LazyLoadTrigger';
import { PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, XCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

interface User {
  id: number;
  email: string;
  name: string;
  isAdmin: boolean;
  isCoach: boolean;
  isPlayer: boolean;
  isParent: boolean;
  isGuest: boolean;
  /** Vendégnél a képviselt külsős csapat neve. */
  guestTeam?: string | null;
  isActive: boolean;
  createdAt: string;
  ageGroups?: string | null;
}

type RoleFilter = 'all' | 'isAdmin' | 'isCoach' | 'isParent' | 'isPlayer' | 'isGuest' | 'inactive';

const ROLE_FILTERS: { id: RoleFilter; label: string }[] = [
  { id: 'all', label: 'Mindenki' },
  { id: 'isAdmin', label: 'Admin' },
  { id: 'isCoach', label: 'Edző' },
  { id: 'isParent', label: 'Szülő' },
  { id: 'isPlayer', label: 'Játékos' },
  { id: 'isGuest', label: 'Vendég' },
  { id: 'inactive', label: 'Inaktív' },
];

const matchesRole = (u: User, f: RoleFilter): boolean =>
  f === 'all' || (f === 'inactive' ? !u.isActive : u[f]);

const Users: React.FC = () => {
  usePageTitle('Felhasználók');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const isMobile = useIsMobile();

  // 800 játékos mellett 1000+ szülői fiók is lehet - keresés és szerepkör-szűrő nélkül nem kezelhető
  const filteredUsers = useMemo(
    () =>
      users.filter(
        (u) =>
          matchesSearch(search, u.name, u.email, u.guestTeam) && matchesRole(u, roleFilter)
      ),
    [users, search, roleFilter]
  );
  const { visible: visibleUsers, hasMore, loadMore } = useBatchedList(filteredUsers, isMobile ? 10 : 50);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await usersAPI.getAll();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setIsFormOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsFormOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setSelectedUser(null);
    loadUsers();
  };

  const handleDelete = async () => {
    if (userToDelete === null) return;

    try {
      await usersAPI.delete(userToDelete);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleToggleActive = async (userId: number) => {
    try {
      await usersAPI.toggleActive(userId);
      loadUsers();
    } catch (error) {
      console.error('Failed to toggle user active status:', error);
    }
  };

  // Egy usernek egyszerre több szerepe is lehet (pl. Edző ÉS Játékos) - ezért
  // badge-ek listáját adjuk vissza, nem egyetlen kizárólagos címkét.
  const getRoleBadges = (user: User) => {
    const badges: { label: string; className: string }[] = [];
    if (user.isAdmin) badges.push({ label: 'Admin', className: 'bg-purple-100 text-purple-800' });
    if (user.isCoach) badges.push({ label: 'Edző', className: 'bg-blue-100 text-blue-800' });
    if (user.isPlayer) badges.push({ label: 'Játékos', className: 'bg-yellow-100 text-yellow-800' });
    if (user.isParent) badges.push({ label: 'Szülő', className: 'bg-green-100 text-green-800' });
    if (user.isGuest) badges.push({ label: user.guestTeam || 'Vendég', className: 'bg-gray-200 text-gray-700' });

    if (badges.length === 0) return null;

    return (
      <span className="flex flex-wrap gap-1">
        {badges.map((b) => (
          <span
            key={b.label}
            className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium ${b.className}`}
          >
            {b.label}
          </span>
        ))}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Betöltés...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Felhasználók</h1>
        <button
          onClick={handleCreate}
          className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Új felhasználó
        </button>
      </div>

      <InvitationsPanel />

      {users.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">Még nincsenek felhasználók hozzáadva.</p>
          <button
            onClick={handleCreate}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Első felhasználó hozzáadása
          </button>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-col gap-3 rounded-lg bg-white p-3 shadow sm:flex-row sm:items-center">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Keresés név vagy email szerint..."
              aria-label="Keresés a felhasználók között"
              className="w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500 sm:w-72"
            />
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Szerepkör">
              {ROLE_FILTERS.map((f) => {
                const count = users.filter((u) => matchesRole(u, f.id)).length;
                if (count === 0 && f.id !== 'all') return null;
                return (
                  <button
                    key={f.id}
                    type="button"
                    aria-pressed={roleFilter === f.id}
                    onClick={() => setRoleFilter(f.id)}
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      roleFilter === f.id ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {f.label} ({count})
                  </button>
                );
              })}
            </div>
          </div>
          {filteredUsers.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-500">Nincs a szűrésnek megfelelő felhasználó.</p>
          )}

          {/* Mobil kártyalista - csak az egyik nézet kerül a DOM-ba */}
          {isMobile && (
          <div className="grid grid-cols-2 gap-3">
            {visibleUsers.map((user) => (
              <div
                key={user.id}
                className={`${user.isActive ? 'bg-white' : 'bg-gray-50'} rounded-lg shadow border p-3 flex flex-col`}
              >
                {/* A keskeny kártyán a név és a badge-ek egymás alatt vannak: egy
                    sorban a négy szerepkör-badge annyira összenyomná a nevet, hogy
                    az betűnként törne. */}
                <div className="mb-1.5 space-y-1">
                  <div className="font-medium text-sm text-gray-900 leading-snug break-words hyphens-auto">
                    {user.name}
                  </div>
                  {getRoleBadges(user)}
                </div>
                <div className="flex-1 space-y-1 text-xs text-gray-600">
                  <div className="flex items-center gap-1 text-gray-500">
                    <EnvelopeIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="truncate">{user.email}</span>
                  </div>
                  <div className="truncate">
                    <span className="text-gray-400">Korosztályok: </span>
                    {user.ageGroups ? (
                      user.ageGroups === 'Összes' ? (
                        <span className="text-gray-400 italic">Összes</span>
                      ) : (
                        <span>{user.ageGroups}</span>
                      )
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                  <div className="truncate">
                    <span className="text-gray-400">Létrehozva: </span>
                    {new Date(user.createdAt).toLocaleDateString('hu-HU')}
                  </div>
                  <button
                    onClick={() => handleToggleActive(user.id)}
                    className="inline-flex items-center"
                  >
                    {user.isActive ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Aktív
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <XCircleIcon className="h-4 w-4 mr-1" />
                        Inaktív
                      </span>
                    )}
                  </button>
                </div>
                <div className="mt-2 flex justify-end gap-1 border-t border-gray-100 pt-1.5">
                  <button
                    onClick={() => handleEdit(user)}
                    className="flex h-9 w-9 items-center justify-center text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-full"
                    aria-label="Szerkesztés"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setUserToDelete(user.id)}
                    className="flex h-9 w-9 items-center justify-center text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-full"
                    aria-label="Törlés"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            {hasMore && (
              <div className="col-span-2">
                <LazyLoadTrigger key={visibleUsers.length} onLoadMore={loadMore} />
              </div>
            )}
          </div>
          )}

          {/* Desktop táblázat */}
          {!isMobile && filteredUsers.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Név
                </th>
                <th scope="col" className="hidden xl:table-cell px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Szerepkör
                </th>
                <th scope="col" className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Korosztályok
                </th>
                <th scope="col" className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Státusz
                </th>
                <th scope="col" className="hidden xl:table-cell px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Létrehozva
                </th>
                <th scope="col" className="px-3 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {visibleUsers.map((user) => (
                <tr key={user.id} className={!user.isActive ? 'bg-gray-50' : ''}>
                  <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.name}</div>
                    {/* Keskenyebb kijelzőn (tablet) az email a név alatt, külön oszlop nélkül */}
                    <div className="text-xs text-gray-500 xl:hidden">{user.email}</div>
                  </td>
                  <td className="hidden xl:table-cell px-3 lg:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                    {getRoleBadges(user)}
                  </td>
                  <td className="px-3 lg:px-6 py-4">
                    {user.ageGroups ? (
                      user.ageGroups === 'Összes' ? (
                        <span className="text-sm text-gray-400 italic">Összes</span>
                      ) : (
                        <span className="text-sm text-gray-600">{user.ageGroups}</span>
                      )
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(user.id)}
                      className="inline-flex items-center"
                    >
                      {user.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Aktív
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Inaktív
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="hidden xl:table-cell px-3 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.createdAt).toLocaleDateString('hu-HU')}
                  </td>
                  <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(user)}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Szerkesztés
                    </button>
                    <button
                      onClick={() => setUserToDelete(user.id)}
                      className="inline-flex items-center px-3 py-1 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Törlés
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hasMore && <LazyLoadTrigger key={visibleUsers.length} onLoadMore={loadMore} />}
          </div>
          )}
        </>
      )}

      <UserForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={handleFormSuccess}
        user={selectedUser}
      />

      <DeleteConfirmModal
        isOpen={userToDelete !== null}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDelete}
        title="Felhasználó törlése"
        message="Biztosan törölni szeretnéd ezt a felhasználót?"
        itemName={users.find(u => u.id === userToDelete)?.name || ''}
      />
    </div>
  );
};

export default Users;
